import axios from 'axios';
import { environment } from '@environments/environment';
import { authentication } from '@ecosystem/newron-client-lib';
import { ShareUser } from '@ecosystem/newron-client-lib/build/sharing/types';
import { AccessLevel } from '@utils/pageConfig/Types';

export interface NotificationDto {
  sharedEntity: SharedEntity;
}

interface SharedEntity {
  entityType: string;
  senderId: string | undefined;
  entityId: number;
  entityPath: string | null;
  entityName: string;
  recipients: Recipient[];
}

export interface Recipient {
  userId: string;
  accessLevel: AccessLevel;
}

export interface NotificationResponseDto {
  requestId: string;
}

//Filter out shared users and exclude users with 'Owner' access level
const getNewRecipients = (eligibleUsers: ShareUser[], currentlySharedUsers: ShareUser[]): Recipient[] => {
  return eligibleUsers
    .filter(
      (user) =>
        !currentlySharedUsers.some((sharedUser) => sharedUser.userId === user.userId) && // Exclude users already in currentlySharedUsers
        user.accessLevel !== AccessLevel.Owner,
    )
    .map((user) => {
      return {
        userId: user.userId,
        accessLevel: user.accessLevel as AccessLevel,
      };
    });
};

//The usage of this method is to get the path after 3rd slash '/'
//Otherwise return empty ''
//Example: https://ecosystem.dev.gfknewron.com/newron-retail/products/leaderboard?view=4U4NO12
//Result : '/newron-retail/products/leaderboard?view=4U4NO12'
export const sanitizedCopyLink = (copyLink: string | null): string => {
  if (!copyLink) return ''; // Return empty if copyLink is null or empty
  const parts = copyLink.split('/');
  return parts.length > 3 ? `/${parts.slice(3).join('/')}` : '';
};

export const paramPreparationForNotificationApi = (
  groupName: string,
  users: ShareUser[],
  currentlySharedUsers: ShareUser[],
  copyLink: string | null,
): NotificationDto => {
  //Map copyLink to the form we want
  const newCopyLink = sanitizedCopyLink(copyLink);
  //Map users to recipient objects and filter out shared users and sender itself
  const newRecipients: Recipient[] = getNewRecipients(users, currentlySharedUsers);

  //get userId to determine whether preference is owner or not
  const userId = authentication.getUser()?.userId;

  return {
    sharedEntity: {
      entityType: 'group', //Has to hardcode to 'group'
      senderId: userId,
      entityId: 2, //Hardcode 2 given by platform
      entityPath: newCopyLink,
      entityName: groupName,
      recipients: newRecipients, // or provide a list of recipients
    },
  } as NotificationDto;
};

export const sendNotification = async (params: NotificationDto): Promise<NotificationResponseDto> => {
  try {
    const response = await axios.post<NotificationResponseDto>(constructNotificationUrl(), params);

    return response.data; // Ensure returning the whole object, not just `requestId`
  } catch (err: any) {
    console.error('Error sending notification:', err.response?.data ?? err.message);

    throw new Error(
      `An error occurred while sending notification: ${err.response?.data?.message ?? err.message ?? 'Unknown error'}`,
    );
  }
};

const constructNotificationUrl = () => {
  return `${environment.baseUrl}/notification`;
};
